/**
 * Mock data adapter for offline development
 */

import { DataSourceAdapter, DataSourceConfig, ApiResponse, RequestConfig } from '../../types';
import { simulateDelay } from './delay';
import { getMockData } from './storage';
import { logger } from '../../../logger';

export class MockAdapter implements DataSourceAdapter {
  public readonly source = 'mock' as const;
  
  constructor(private config: DataSourceConfig) {
    if (config.logMockCalls) {
      logger.info(
        '%c📡 Mock Data Adapter initialized',
        'color: #22c55e; font-weight: bold;',
        { delay: `${config.mockDelay}ms` }
      );
    }
  }

  async get<T = Record<string, unknown>>(url: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest('GET', url, undefined, config);
  }

  async post<T = Record<string, unknown>, D = unknown>(url: string, data?: D, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest('POST', url, data, config);
  }

  async put<T = Record<string, unknown>, D = unknown>(url: string, data?: D, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest('PUT', url, data, config);
  }

  async patch<T = Record<string, unknown>, D = unknown>(url: string, data?: D, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest('PATCH', url, data, config);
  }

  async delete<T = Record<string, unknown>>(url: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest('DELETE', url, undefined, config);
  }

  private async makeRequest<T>(
    method: string,
    url: string,
    data?: unknown,
    config?: RequestConfig
  ): Promise<ApiResponse<T>> {
    // Log the request if enabled
    if (this.config.logMockCalls) {
      logger.debug(
        `%c🔄 [MOCK] ${method} ${url}`,
        'color: #3b82f6; font-weight: bold;',
        data ? { data } : ''
      );
    }

    // Simulate network delay
    if (this.config.mockDelay) {
      await simulateDelay(this.config.mockDelay);
    }

    const requestUrl = this.appendQueryParams(url, config?.params);

    try {
      // Get mock response from storage
      const mockResponse = getMockData(method, requestUrl, data, config);

      // Log successful response
      if (this.config.logMockCalls) {
        logger.debug(
        `%c✅ [MOCK] ${method} ${requestUrl} - Success`,
          'color: #22c55e; font-weight: bold;',
          mockResponse
        );
      }

      return {
        data: mockResponse as T,
        status: 200,
        message: 'Success'
      };
    } catch (error: unknown) {
      // Log error response
      if (this.config.logMockCalls) {
        logger.warn(
          `%c❌ [MOCK] ${method} ${requestUrl} - Error`,
          'color: #ef4444; font-weight: bold;',
          error
        );
      }

      // Transform mock error to match API error format
      if (error && typeof error === 'object' && 'response' in error) {
        throw error; // Already in the right format
      }

      // Wrap unexpected errors
      const errorMessage = error instanceof Error ? error.message : 'Mock data error';
      throw {
        response: {
          status: 500,
          data: {
            message: errorMessage,
            status: 500
          }
        }
      };
    }
  }

  private appendQueryParams(url: string, params?: RequestConfig['params']): string {
    if (!params || typeof params !== 'object') {
      return url;
    }

    const searchParams = new URLSearchParams();
    Object.entries(params as Record<string, unknown>).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return;
      }

      if (Array.isArray(value)) {
        value
          .filter((item) => item !== undefined && item !== null)
          .forEach((item) => {
            searchParams.append(key, String(item));
          });
        return;
      }

      searchParams.set(key, String(value));
    });

    if (!searchParams.toString()) {
      return url;
    }

    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${searchParams.toString()}`;
  }
}
